import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { MUI_TABLE_PAGINATION_OPTIONS } from '../../components/MuiTablePagination'
import dayjs from '../../utils/dayjsConfig'
import { SETTLEMENT_LIST_CONFIG } from '../constants/constants'
import { SETTLEMENT_FILTER_KEYS } from './indexFilters'
import { createEmptyDateRange } from '../../report/unified/helpers/routeExecutionFilters'

const positiveInteger = (value, fallback) => {
  const number = Number(value)
  return Number.isInteger(number) && number > 0 ? number : fallback
}

// Guarda los filtros aplicados del listado en su URL y los restaura al navegar.
export default function useSettlementsListFilters() {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const queryParams = useMemo(() => new URLSearchParams(search), [search])
  const query = queryParams.get('q') || ''
  const [searchInput, setSearchInput] = useState(query)
  const currentPage = positiveInteger(queryParams.get('page'), SETTLEMENT_LIST_CONFIG.INITIAL_PAGE)
  const requestedPerPage = Number(queryParams.get('per_page'))
  const perPage = MUI_TABLE_PAGINATION_OPTIONS.includes(requestedPerPage)
    ? requestedPerPage
    : SETTLEMENT_LIST_CONFIG.PER_PAGE
  const allSelectedOptions = useMemo(() => {
    const options = new Map()
    const add = (key, parameter) => {
      const values = (queryParams.get(parameter) || '').split(',').filter(Boolean)
      if (values.length) options.set(key, values)
    }
    add(SETTLEMENT_FILTER_KEYS.CREATED_BY, 'client_uid')
    add(SETTLEMENT_FILTER_KEYS.STATUS, 'status')
    return options
  }, [queryParams])
  const dateRange = useMemo(() => {
    const start = queryParams.get('start_date')
    const end = queryParams.get('end_date')
    if (!start || !end) return createEmptyDateRange()
    const range = { start: dayjs(start), end: dayjs(end) }
    return range.start.isValid() && range.end.isValid() && !range.end.isBefore(range.start)
      ? range
      : createEmptyDateRange()
  }, [queryParams])

  useEffect(() => setSearchInput(query), [query])

  const update = (changes) => {
    const next = new URLSearchParams(queryParams)
    // Cada cambio de filtro vuelve a la primera página; la paginación puede sobrescribirla.
    next.delete('page')
    Object.entries(changes).forEach(([key, value]) => {
      if (value == null || value === '' || (key === 'page' && value === 1)) next.delete(key)
      else next.set(key, value)
    })
    const nextSearch = next.toString()
    navigate({ pathname, search: nextSearch ? `?${nextSearch}` : '' })
  }

  return {
    query,
    searchInput,
    setSearchInput,
    currentPage,
    perPage,
    allSelectedOptions,
    dateRange,
    update
  }
}
